/* eslint-disable complexity */
import { useContext, useRef, useEffect, memo } from 'react';
import cn from 'classnames';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';

import { useChatLayoutStore } from './hooks/useChatLayoutStore';
import {
  useChatStoreExtract,
  useLastResponseGeneratorStep,
  useMostRecentResponse,
  useStepParams,
  useStepResponse,
  useIsStepSequentiallySkippedAndVisible,
  useIsStepVisible,
  useIsStepConditionallyVisible,
  useNumberOfTurns,
  useIsStepCompleted,
  useIsCurrentStep,
  useTurnVersion,
  useIsRealCustomerAudit,
} from '../Chat/hooks/ChatStore.hooks';
import {
  ContextType,
  HIDDEN_STEP_TYPES,
  InteractionStepType,
  MessageRole,
  StepParamsForType,
} from '@scale/shared/types/chat';
import { EditableChatMessage } from './EditableChatMessage';
import { memoize } from 'proxy-memoize';
import { ChatStoreState } from '../Chat/hooks/types';
import { getReadOnlyOverrideForStep, isPromptEditableAtIndex } from './utils';
import { StepWidthWrapper } from './step/StepWidthWrapper';
import ContributorFeedbackBox from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback';
import { isStepVisible as checkIfStepIsVisible } from '@scale/shared/chat/api';
import { CardAlert } from './TempComponents';
import { ReviewLevel } from '@scale/shared-types';
import { getStepDescriptorByType } from './definitions';
import { StepWithMaybeResponseProps } from './StepWithMaybeResponse';
import { StepParamsAuxiliary } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.computed';
import { extractTextFromRegex } from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';

/**
 * Determines whether the unified feedback box should be rendered for a given step.
 * If this function returns true, we should not even render the placeholder div that
 * maintains the two-column layout.
 */
const shouldRenderPerStepUnifiedFeedbackBoxOrPlaceholder = (
  stepParams: StepParamsForType<InteractionStepType> & StepParamsAuxiliary,
  isCompletedTurnView?: boolean,
): boolean => {
  switch (stepParams.type) {
    case InteractionStepType.ModelResponseSelector:
      return !stepParams.params.skip_selection;
    case InteractionStepType.PromptInput:
      return false;
    case InteractionStepType.MultiTurnContinue:
      return !isCompletedTurnView;
    default:
      return true;
  }
};

function StepWithMaybeResponseView({
  responseIndex,
  isPinnedView = false,
  collapsibleInstruction,
  readOnly,
  preventScrollToBottom,
  inlineMode = true,
  isCompletedTurnView,
  extractModelResponseRegex,
}: StepWithMaybeResponseProps) {
  const stepRef = useRef<HTMLDivElement>(null);
  const taskContext = useContext(ChatTaskContext);

  const numOfTurns = useNumberOfTurns(responseIndex);
  const turnIndex = numOfTurns - 1;

  // TODO (Matthew / Lajos): Deprecate in favor of emitting a clear local state event
  const turnVersion = useTurnVersion(turnIndex);

  const config = useChatStoreExtract(state => state.config);

  const lastGeneratorStep = useLastResponseGeneratorStep();
  const response = useStepResponse(responseIndex, true);
  const complete = useIsStepCompleted(responseIndex, true);
  const stepParams = useStepParams(responseIndex);
  const readOnlyOverride = getReadOnlyOverrideForStep(config, stepParams);
  const isRealCustomerAudit = useIsRealCustomerAudit();

  /*
   * We don't need to narrow down the responses here with responseIndex, because we need the actual last message
   * This is because we only use lastMessage in scenarios where it definitely represents the last message in the chat
   * (i.e. there are no future turns that exist with a lastMessage that would overwrite it)
   */
  const lastMessage = useMostRecentResponse();
  const isStepSequentiallySkippedAndVisible = useIsStepSequentiallySkippedAndVisible(responseIndex);
  const isStepVisible = useIsStepVisible(responseIndex);
  const isStepConditionallyVisible = useIsStepConditionallyVisible(responseIndex);

  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);

  const isCurrentStep = useIsCurrentStep(responseIndex);

  useEffect(() => {
    if (isCurrentStep && stepRef.current && preventScrollToBottom !== true) {
      stepRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isCurrentStep, preventScrollToBottom]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const step = getStepDescriptorByType(stepParams.type);

  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);

  const shouldUseUnifiedFeedback = stepParams && !isPinnedView;

  const memoizedGetLinterMetadata = memoize((state: ChatStoreState) => {
    return state.stepLintersMetadata.at(responseIndex);
  });
  const linterMetadata = useChatStoreExtract(state => memoizedGetLinterMetadata(state));
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const numberOfTurnsUntilIndex = useNumberOfTurns(responseIndex);

  const isSkipped = response?.contextType === ContextType.Skipped;

  const requiresResponse = isSkipped && checkIfStepIsVisible(stepParams, reviewLevel);

  const errorMessage =
    requiresResponse && !taskContext.isInitialAttempt
      ? 'Please complete the section below.'
      : linterMetadata?.hasLinterError
      ? `Please fix the linter error. ${
          linterMetadata?.hasIncompleteFrontendLinterFeedback
            ? 'Please rate the linter before submission.'
            : ''
        }`
      : undefined;

  const promptTurnIndex =
    stepParams.type === InteractionStepType.PromptInput ? 0 : numberOfTurnsUntilIndex;

  const isPrompt = [
    InteractionStepType.PromptInput,
    InteractionStepType.MultiTurnContinue,
  ].includes(stepParams.type);
  const isPromptAndShouldBeEditable = isPrompt && isPromptEditableAtIndex(config, promptTurnIndex);
  const isReadOnlyOnReviewLevel =
    (!stepParams.params.editable_during_consensus && reviewLevel === ReviewLevel.ReviewConsensus) ||
    stepParams.params.readonly_review_levels?.includes(reviewLevel);
  const finalReadOnly =
    (isPrompt && !isPromptAndShouldBeEditable) ||
    readOnly ||
    readOnlyOverride ||
    isReadOnlyOnReviewLevel;

  if (!step) return null;

  if (isPinnedView && !step.isPinnable) return null;

  if (isCurrentStep && (readOnly || readOnlyOverride)) return null;

  if (!isStepVisible) return null;

  if (!isStepConditionallyVisible) return null;

  if (isStepSequentiallySkippedAndVisible) return null;

  if (errorMessage && isRealCustomerAudit) return null;
  // TODO: @Lajos move the below EditableChatMessage to MultiTurnContinueTaskComponent
  return (
    <>
      {stepParams.type === InteractionStepType.MultiTurnContinue &&
        !response &&
        lastMessage.length > 0 &&
        inlineMode && (
          <div className="flex flex-row">
            <StepWidthWrapper>
              <EditableChatMessage
                turnIndex={turnIndex}
                message={{
                  role: MessageRole.Assistant,
                  content: extractModelResponseRegex
                    ? extractTextFromRegex(lastMessage, extractModelResponseRegex)
                    : lastMessage,
                }}
                enableRTLText={
                  lastGeneratorStep && 'enable_rtl_text' in lastGeneratorStep.params
                    ? lastGeneratorStep.params.enable_rtl_text
                    : false
                }
                hideErrors
              />
            </StepWidthWrapper>
            {shouldUseUnifiedFeedback && <ContributorFeedbackBox className="ml-4" isHidden />}
          </div>
        )}
      <div
        className={cn('relative flex flex-row border-transparent', {
          'border rounded-md transition-border duration-150': isDrawerOpen && !isPinnedView,
          'w-full': isPinnedView,
        })}
        id={`step-${responseIndex}`}
        ref={stepRef}
      >
        <CardAlert message={errorMessage}>
          {isPinnedView && step.PinnedViewComponent ? (
            <step.PinnedViewComponent
              key={`turn-${turnIndex}-step-${responseIndex}-${turnVersion}-pinned`}
              responseIndex={responseIndex}
              skipContext={isSkipped ? response?.context : undefined}
              complete={isSkipped ? false : complete}
              readOnly
              collapsibleInstruction={collapsibleInstruction}
            />
          ) : (
            <step.ViewComponent
              key={`turn-${turnIndex}-step-${responseIndex}-${turnVersion}-view`}
              responseIndex={responseIndex}
              skipContext={isSkipped ? response?.context : undefined}
              complete={isSkipped ? false : complete}
              readOnly={finalReadOnly}
              collapsibleInstruction={collapsibleInstruction}
            />
          )}
        </CardAlert>
        {shouldUseUnifiedFeedback &&
          shouldRenderPerStepUnifiedFeedbackBoxOrPlaceholder(stepParams, isCompletedTurnView) && (
            <ContributorFeedbackBox
              className="ml-4"
              step={stepParams}
              isHidden={HIDDEN_STEP_TYPES.includes(stepParams.type)}
            />
          )}
      </div>
    </>
  );
}

export default memo(StepWithMaybeResponseView);
